import { Controller } from 'stimulus';

// Drives the battery builder (version B): a single ordered list of member
// tests. Templates are added from a catalog picker; each row carries a hidden
// task_id, a drag-updated position, and an editable (repeatable) sequence
// number. sortable-cocoon handles drag reordering of the `.position` inputs.
export default class extends Controller {
  static targets = ['list', 'template', 'picker', 'count', 'empty'];

  // --- catalog picker ---------------------------------------------------
  openPicker(event) {
    if (event) event.preventDefault();
    this.pickerTarget.classList.remove('hidden');
  }

  closePicker(event) {
    if (event) event.preventDefault();
    this.pickerTarget.classList.add('hidden');
  }

  addItem(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const { taskId, taskName } = button.dataset;

    const index = new Date().getTime();
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, index);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    const row = wrapper.firstElementChild;

    row.querySelector('[data-role="task-id"]').value = taskId;
    row.querySelector('[data-role="name"]').textContent = taskName;
    const seq = row.querySelector('[data-role="sequence"]');
    if (seq) seq.value = this.nextSequence();

    this.listTarget.appendChild(row);
    this.renumber();
    this.flashAdded(button);
  }

  // Brief "Added!" confirmation on the picker button: swap the blue button
  // classes for their green variants (button_class is color-parameterized),
  // then restore. Re-clicks during the flash still add rows — they just
  // restart the timer.
  flashAdded(button) {
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent;
      button.dataset.originalClass = button.className;
    }
    button.textContent = 'Added!';
    button.className = button.dataset.originalClass.replace(/blue/g, 'green');
    // Drop focus so the focus ring doesn't linger (and get clipped at the
    // scroll container's left edge) after the flash reverts.
    button.blur();

    clearTimeout(button.flashTimer);
    button.flashTimer = setTimeout(() => {
      button.textContent = button.dataset.originalText;
      button.className = button.dataset.originalClass;
      delete button.dataset.originalText;
      delete button.dataset.originalClass;
    }, 1200);
  }

  removeItem(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('.battery-item');
    const id = row.querySelector('[data-role="id"]');

    if (id && id.value) {
      // persisted — flag for destruction and hide
      const destroy = row.querySelector('[data-role="destroy"]');
      if (destroy) destroy.value = '1';
      row.classList.add('hidden');
    } else {
      row.remove();
    }
    this.renumber();
  }

  // --- helpers ----------------------------------------------------------
  get activeRows() {
    return Array.from(this.listTarget.querySelectorAll('.battery-item')).filter((row) => {
      if (row.classList.contains('hidden')) return false;
      const destroy = row.querySelector('[data-role="destroy"]');
      return !(destroy && destroy.value === '1');
    });
  }

  nextSequence() {
    const seqs = this.activeRows
      .map((row) => parseInt(row.querySelector('[data-role="sequence"]').value, 10))
      .filter((n) => !Number.isNaN(n));
    return seqs.length ? Math.max(...seqs) + 1 : 1;
  }

  renumber() {
    this.activeRows.forEach((row, i) => {
      const pos = row.querySelector('.position');
      if (pos) pos.value = i;
    });
    if (this.hasCountTarget) this.countTarget.textContent = this.activeRows.length;
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle('hidden', this.activeRows.length > 0);
    }
  }
}
